import Swiper from 'swiper';
import { Pagination, Autoplay, EffectFade, Navigation } from 'swiper/modules';
import { Accordion } from './core';

/**
 * Home page hero slider. The pagination bullet label comes from the
 * data-label-goto attribute of the slider container (translation alias).
 */
class HeroSlider {
	constructor(selector) {
		this.container = document.querySelector(selector);
		if (!this.container) return;

		this.init();
	}

	init() {
		const paginationEl = this.container.querySelector('.hero__pagination');
		const nextEl = this.container.querySelector('.hero__arrow-btn--next');
		const prevEl = this.container.querySelector('.hero__arrow-btn--prev');
		const labelGoto = this.container.dataset.labelGoto ?? '';

		this.instance = new Swiper(this.container, {
			modules: [Pagination, Autoplay, EffectFade, Navigation],
			loop: true,
			speed: 800,
			effect: 'fade',
			fadeEffect: { crossFade: true },

			// Slides change every 5 s, also after the user clicks the arrows or dots
			autoplay: {
				delay: 5000,
				disableOnInteraction: false,
				pauseOnMouseEnter: true,
			},

			pagination: {
				el: paginationEl || '.hero__pagination',
				clickable: true,
				renderBullet: (index, className) => {
					const bullet = document.createElement('button');
					bullet.className = className;
					bullet.setAttribute('aria-label', labelGoto.replace(':number:', String(index + 1)));

					return bullet.outerHTML;
				},
			},

			navigation: {
				nextEl: nextEl || '.hero__arrow-btn--next',
				prevEl: prevEl || '.hero__arrow-btn--prev',
			},

			on: {
				init: (swiper) => this.updateA11y(swiper),
				slideChangeTransitionEnd: (swiper) => this.updateA11y(swiper)
			}
		});
	}

	updateA11y(swiper) {
		swiper.slides.forEach((slide) => {
			const focusableElements = slide.querySelectorAll('a, button');
			const isActive = slide.classList.contains('swiper-slide-active');
			slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
			focusableElements.forEach(el => el.setAttribute('tabindex', isActive ? '0' : '-1'));
		});
	}
}

/**
 * Класс для управления галереей товаров
 */
class ProductGallery {
	constructor() {
		this.items = document.querySelectorAll('.products__item');
		this.groups = document.querySelectorAll('.products__image-group');
		this.btnPrev = document.querySelector('.products__btn--prev');
		this.btnNext = document.querySelector('.products__btn--next');

		if (!this.items.length || !this.groups.length) return;

		this.currentIndex = 0;
		this.init();
	}

	init() {
		this.bindEvents();
	}

	bindEvents() {
		this.items.forEach((item, index) => {
			item.addEventListener('mouseenter', () => this.update(index));
			item.addEventListener('click', () => this.update(index));
		});

		if (this.btnPrev) {
			this.btnPrev.addEventListener('click', () => this.prev());
		}
		if (this.btnNext) {
			this.btnNext.addEventListener('click', () => this.next());
		}
	}

	update(index) {
		if (this.currentIndex === index) return;

		this.items[this.currentIndex].classList.remove('products__item--active');
		this.groups[this.currentIndex].classList.remove('products__image-group--active', '_active');

		this.currentIndex = index;

		this.items[this.currentIndex].classList.add('products__item--active');
		this.groups[this.currentIndex].classList.add('products__image-group--active');
	}

	prev() {
		const index = (this.currentIndex === 0) ? this.items.length - 1 : this.currentIndex - 1;
		this.update(index);
	}

	next() {
		const index = (this.currentIndex === this.items.length - 1) ? 0 : this.currentIndex + 1;
		this.update(index);
	}
}

/**
 * Класс для слайдера фактов с анимацией чисел
 */
class FactsSlider {
	constructor(selector) {
		this.el = document.querySelector(selector);
		if (!this.el) return;

		this.init();
	}

	init() {
		const parent = this.el.parentElement;
		const nextEl = parent.querySelector('.facts-slider__next') || '.facts-slider__next';
		const prevEl = parent.querySelector('.facts-slider__prev') || '.facts-slider__prev';

		this.instance = new Swiper(this.el, {
			modules: [Navigation],
			slidesPerView: 'auto',
			loop: true,
			a11y: { enabled: true },
			speed: 600,
			watchSlidesProgress: true,
			effect: 'creative',

			breakpoints: {
				320: {
					spaceBetween: 16,
				},
				1024: {
					spaceBetween: 64,
				},
			},

			navigation: {
				nextEl: nextEl,
				prevEl: prevEl,
			},

			on: {
				init: (swiper) => {
					this.animateActiveNumber(swiper.slides[swiper.activeIndex]);
				},
				slideChangeTransitionStart: (swiper) => {
					this.animateActiveNumber(swiper.slides[swiper.activeIndex]);
				}
			}
		});
	}

	animateActiveNumber(activeSlide) {
		if (!activeSlide) return;

		const numberElement = activeSlide.querySelector('.js-facts-number');
		if (!numberElement) return;

		if (!numberElement.dataset.target) {
			numberElement.dataset.target = numberElement.textContent.trim();
		}

		const targetString = numberElement.dataset.target;
		const targetNumber = parseInt(targetString.replace(/[^\d]/g, '')) || 0;
		const suffix = targetString.replace(/[\d\s]/g, '');

		if (targetNumber === 0) return;

		const duration = 600;
		let startTimestamp = null;

		if (numberElement.animationFrame) {
			cancelAnimationFrame(numberElement.animationFrame);
		}

		const step = (timestamp) => {
			if (!startTimestamp) startTimestamp = timestamp;
			const progress = Math.min((timestamp - startTimestamp) / duration, 1);
			const currentNumber = Math.floor(progress * targetNumber);
			numberElement.textContent = currentNumber + suffix;

			if (progress < 1) {
				numberElement.animationFrame = requestAnimationFrame(step);
			} else {
				numberElement.textContent = targetString;
			}
		};

		numberElement.animationFrame = requestAnimationFrame(step);
	}
}

class ImageFeatures {
	constructor(elementOrSelector) {
		this.section = typeof elementOrSelector === 'string'
			? document.querySelector(elementOrSelector)
			: elementOrSelector;

		if (!this.section) return;

		this.images = Array.from(this.section.querySelectorAll('[data-drive-features__image]'));
		this.currentIndex = 0;
		this.activeImageClass = 'drive-features__image--active';
		this.pendingUrl = null;

		const accordionElement = this.section.querySelector('[data-features-accordion]');

		// Инициализируем только если есть аккордеон и хотя бы 2 картинки для "пинг-понга"
		if (accordionElement && this.images.length >= 2) {
			this.accordion = new Accordion(accordionElement, {
				onChange: (activeItem) => {
					// Используем dataset (эквивалентно getAttribute('data-image'))
					const newImageUrl = activeItem.dataset.image;
					this.changeImage(newImageUrl);
				}
			});
		}
	}

	changeImage(url) {
		if (!url) return;

		const currentImg = this.images[this.currentIndex];

		if (currentImg.getAttribute('src') === url) return;

		const nextIndex = this.currentIndex === 0 ? 1 : 0;
		const nextImg = this.images[nextIndex];

		this.pendingUrl = url;

		nextImg.onload = () => {
			if (this.pendingUrl !== url) return;
			nextImg.classList.add(this.activeImageClass);
			currentImg.classList.remove(this.activeImageClass);
			this.currentIndex = nextIndex;
			this.clearHandlers(nextImg);
		};

		nextImg.onerror = () => {
			console.error(`Ошибка загрузки изображения: ${url}`);
			this.clearHandlers(nextImg);
		};

		nextImg.src = url;
	}

	clearHandlers(img) {
		img.onload = null;
		img.onerror = null;
	}
}

class MapFeatures {
	constructor(sectionSelector) {
		this.section = document.querySelector(sectionSelector);
		if (!this.section) return;

		this.countriesGroup = this.section.querySelector('svg #countries');
		this.labelGroups = this.section.querySelectorAll('#labels_big, #labels_small');
		this.countryLinks = this.loadCountryLinks();

		const accordionElement = this.section.querySelector('[data-map-accordion]');

		if (accordionElement) {
			this.accordion = new Accordion(accordionElement, {
				onChange: (activeItem) => {
					this.highlightCountryOnMap(activeItem.dataset.countries, activeItem.dataset.labels);
				}
			});
		}

		const activeItemOnLoad = this.section.querySelector('[data-map-accordion] .accordion__item--active');
		if (activeItemOnLoad) {
			this.highlightCountryOnMap(activeItemOnLoad.dataset.countries, activeItemOnLoad.dataset.labels);
		}

		if (this.countriesGroup) {
			this.bindEvents();
		}
	}

	bindEvents() {
		this.countriesGroup.addEventListener('mouseover', (e) => this.handleHover(e, true));
		this.countriesGroup.addEventListener('mouseout', (e) => this.handleHover(e, false));
		this.countriesGroup.addEventListener('click', (e) => this.handleClick(e));
	}

	handleHover(e, isHovering) {
		const country = e.target.closest('path');

		if (!country || !country.classList.contains('active-country')) return;

		const countryId = country.id || (country.parentElement && country.parentElement.id);
		if (!countryId) return;

		const label = this.findLabelForCountry(countryId);
		if (label) {
			label.classList.toggle('label-hovered', isHovering);
		}
	}

	handleClick(e) {
		const activeCountryElement = e.target.closest('.active-country');
		if (!activeCountryElement) return;

		const countryId = this.getCountryCode(activeCountryElement);
		const countryUrl = countryId && this.countryLinks[countryId];
		if (countryUrl) {
			window.location.href = countryUrl;
			return;
		}

		const activeItem = this.section.querySelector('[data-map-accordion] .accordion__item--active');
		if (!activeItem) return;

		const linkBtn = activeItem.querySelector('.accordion__btn');
		if (linkBtn && linkBtn.hasAttribute('href')) {
			const url = linkBtn.getAttribute('href');
			if (url && url !== '#') {
				window.location.href = url;
			}
		}
	}

	getCountryCode(el) {
		return el.dataset.country || el.id || (el.parentElement && el.parentElement.id) || null;
	}

	loadCountryLinks() {
		const scriptEl = document.querySelector('[data-where-to-buy-links]');
		if (!scriptEl) return {};

		try {
			return JSON.parse(scriptEl.textContent) || {};
		} catch (error) {
			console.error('Ошибка парсинга ссылок карты (MapFeatures):', error);
			return {};
		}
	}

	findLabelForCountry(countryId) {
		const visibleGroup = this.section.querySelector('#labels_small.is-visible, #labels_big.is-visible');
		if (!visibleGroup) return null;
		return visibleGroup.querySelector(`[data-country="${countryId}"]`);
	}

	highlightCountryOnMap(countriesData, labelId) {
		this.section.querySelectorAll('#countries .active-country').forEach(el => {
			el.classList.remove('active-country');
		});

		if (countriesData) {
			try {
				const countriesToHighlight = JSON.parse(countriesData);
				countriesToHighlight.forEach(countryId => {
					const countryNode = this.section.querySelector(`svg #${countryId}`);
					if (countryNode) {
						countryNode.classList.add('active-country');
						countryNode.querySelectorAll('path').forEach(p => p.classList.add('active-country'));
					}
				});
			} catch (error) {
				console.error('Ошибка парсинга списка стран (MapFeatures):', error);
			}
		}

		if (this.labelGroups.length) {
			this.labelGroups.forEach(group => group.classList.remove('is-visible'));

			if (labelId) {
				const activeLabelGroup = this.section.querySelector(`svg #${labelId}`);
				if (activeLabelGroup) activeLabelGroup.classList.add('is-visible');
			}
		}
	}
}


// class ScrollAnimator {
// 	constructor(options = {}) {
// 		this.designW = options.designW || 1920;
// 		this.designH = options.designH || 1180;
//
// 		this.section = document.querySelector(options.sectionSelector || '#scrollSection');
// 		this.stage = document.querySelector(options.stageSelector || '#stage');
// 		this.items = document.querySelectorAll(options.itemSelector || '.item');
//
// 		this.ticking = false;
//
// 		this.onScroll = this.onScroll.bind(this);
// 		this.onResize = this.onResize.bind(this);
// 		this.update = this.update.bind(this);
//
// 		this.init();
// 	}
//
// 	init() {
// 		if (!this.section || !this.stage || !this.items.length) {
// 			console.warn('ScrollAnimator: Не найдены необходимые DOM-элементы.');
// 			return;
// 		}
//
// 		window.addEventListener('scroll', this.onScroll, { passive: true });
// 		window.addEventListener('resize', this.onResize);
//
// 		this.scaleStage();
// 		this.update();
// 	}
//
// 	destroy() {
// 		window.removeEventListener('scroll', this.onScroll);
// 		window.removeEventListener('resize', this.onResize);
// 	}
//
// 	ease(t) {
// 		return t < 0.5
// 			? 4 * t * t * t
// 			: 1 - Math.pow(-2 * t + 2, 3) / 2;
// 	}
//
// 	scaleStage() {
// 		const scale = Math.min(
// 			window.innerWidth / this.designW,
// 			window.innerHeight / this.designH
// 		);
// 		this.stage.style.transform = `scale(${scale})`;
// 	}
//
// 	update() {
// 		this.ticking = false;
//
// 		const rect = this.section.getBoundingClientRect();
// 		const vh = window.innerHeight;
// 		const total = this.section.offsetHeight;
//
// 		let progress = (-rect.top) / total;
// 		progress = Math.max(0, Math.min(1, progress));
//
// 		const PIN_END = (total - vh) / total;
// 		const CONVERGE_END = PIN_END * 0.5;
// 		const HOLD_END = CONVERGE_END + 0.02;
//
// 		let masterK;
// 		if (progress < CONVERGE_END) {
// 			masterK = progress / CONVERGE_END;
// 		} else if (progress < HOLD_END) {
// 			masterK = 1;
// 		} else {
// 			masterK = 1 - (progress - HOLD_END) / (1 - HOLD_END);
// 		}
//
// 		this.items.forEach(item => {
// 			const img = item.querySelector('img');
// 			if (!img) return;
//
// 			const dataset = item.dataset;
// 			const fromX = parseFloat(dataset.fromX) || 0;
// 			const fromY = parseFloat(dataset.fromY) || 0;
// 			const scaleXFrom = parseFloat(dataset.scaleXFrom);
// 			const scaleYFrom = parseFloat(dataset.scaleYFrom);
// 			const clipXFrom = parseFloat(dataset.clipXFrom);
// 			const clipYFrom = parseFloat(dataset.clipYFrom);
// 			const opacityFrom = parseFloat(dataset.opacityFrom);
// 			const kStart = parseFloat(dataset.kStart) || 0;
// 			const kEnd = parseFloat(dataset.kEnd) || 1;
//
// 			let localK = (masterK - kStart) / (kEnd - kStart);
// 			localK = Math.max(0, Math.min(1, localK));
// 			const ek = this.ease(localK);
//
// 			const tx = fromX * (1 - ek);
// 			const ty = fromY * (1 - ek);
//
// 			let transform = `translate(${tx}px, ${ty}px)`;
//
// 			if (!isNaN(scaleXFrom) || !isNaN(scaleYFrom)) {
// 				const sx = !isNaN(scaleXFrom) ? scaleXFrom + (1 - scaleXFrom) * ek : 1;
// 				const sy = !isNaN(scaleYFrom) ? scaleYFrom + (1 - scaleYFrom) * ek : 1;
// 				transform += ` scale(${sx}, ${sy})`;
// 			}
//
// 			img.style.transform = transform;
//
// 			if (!isNaN(opacityFrom)) {
// 				img.style.opacity = opacityFrom + (1 - opacityFrom) * ek;
// 			}
//
// 			if (!isNaN(clipXFrom) || !isNaN(clipYFrom)) {
// 				const cx = !isNaN(clipXFrom) ? clipXFrom * (1 - ek) : 0;
// 				const cy = !isNaN(clipYFrom) ? clipYFrom * (1 - ek) : 0;
// 				img.style.clipPath = `inset(${cy}% ${cx}% ${cy}% ${cx}%)`;
// 			}
// 		});
// 	}
//
// 	onScroll() {
// 		if (!this.ticking) {
// 			requestAnimationFrame(this.update);
// 			this.ticking = true;
// 		}
// 	}
//
// 	onResize() {
// 		this.scaleStage();
// 		this.update();
// 	}
// }

class CommentsSlider {
	constructor(selector) {
		this.container = document.querySelector(selector);
		if (!this.container) return;

		new Swiper(this.container, {
			modules: [Navigation],
			loop: false,
			spaceBetween: 32,
			slidesPerView: 1,
			autoHeight: true,
			navigation: {
				nextEl: this.container.parentElement.querySelector('.comments__nav-btn--next'),
				prevEl: this.container.parentElement.querySelector('.comments__nav-btn--prev'),
			},
			breakpoints: {
				768: { slidesPerView: 2 },
				1024: { slidesPerView: 3 }
			}
		});
	}
}

class NewsSlider {
	constructor(selector) {
		this.container = document.querySelector(selector);
		if (!this.container) return;

		const wrapper = this.container.closest('.news-slider');

		new Swiper(this.container, {
			loop: true,
			modules: [Navigation],
			spaceBetween: 64,
			grabCursor: true,
			slidesPerView: 'auto',
			navigation: {
				nextEl: wrapper?.querySelector('.news-slider__btn--next'),
				prevEl: wrapper?.querySelector('.news-slider__btn--prev'),
			},
			breakpoints: {
				320: { slidesPerView: 1 },
				768: { slidesPerView: 2, spaceBetween: 32 },
				1024: { slidesPerView: 2.2 },
				1400: { slidesPerView: 2.4 }
			}
		});
	}
}


/**
 * Инициализация всех компонентов
 */
document.addEventListener('DOMContentLoaded', () => {
	new HeroSlider('[data-hero-slider]');

	new ProductGallery();

	new FactsSlider('[data-facts-slider-swiper]');

	const featureSections = document.querySelectorAll('[data-drive-features]');

	featureSections.forEach(section => {
		new ImageFeatures(section);
	});

	new MapFeatures('[data-map-section]');

	// const animator = new ScrollAnimator();

	new CommentsSlider('[data-comments-slider]');

	new NewsSlider('[data-news-slider]');
});
